import classNames from 'classnames';
import { useEffect, useImperativeHandle, useRef } from 'react';
import { forwardRef } from 'react';
import styles from './drag-scroll.module.scss';

interface DragScrollProps {
  children: React.ReactNode;
  className?: string;
}

const DragScroll = forwardRef<HTMLDivElement, DragScrollProps>(
  ({ children, className }, ref) => {
    const componentRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => componentRef.current as HTMLDivElement, [
      componentRef,
    ]);

    useEffect(() => {
      const element = componentRef.current;

      if (!element) return;

      let isDown = false;
      let startX: number;
      let startY: number;
      let initialScrollLeft: number;
      let initialScrollTop: number;

      const handleDrag = (event: MouseEvent) => {
        event.preventDefault();

        if (!isDown) return;

        const { offsetTop, offsetLeft } = element;
        const { pageX, pageY } = event;

        const x = pageX - offsetLeft;
        const y = pageY - offsetTop;
        const walkX = x - startX;
        const walkY = y - startY;

        element.scrollLeft = initialScrollLeft - walkX;
        element.scrollTop = initialScrollTop - walkY;
      };

      const handleDragStart = (event: MouseEvent) => {
        event.preventDefault();

        const { offsetLeft, offsetTop, scrollLeft, scrollTop } = element;
        const { pageX, pageY } = event;

        isDown = true;
        startX = pageX - offsetLeft;
        startY = pageY - offsetTop;
        initialScrollLeft = scrollLeft;
        initialScrollTop = scrollTop;

        element.style.cursor = 'grabbing';
      };

      const handleDragStop = () => {
        isDown = false;
        element.style.cursor = '';
      };

      element.addEventListener('pointerdown', handleDragStart);
      element.addEventListener('pointerleave', handleDragStop);
      element.addEventListener('pointermove', handleDrag);
      element.addEventListener('pointerup', handleDragStop);

      return () => {
        element.removeEventListener('pointerdown', handleDragStart);
        element.removeEventListener('pointerleave', handleDragStop);
        element.removeEventListener('pointermove', handleDrag);
        element.removeEventListener('pointerup', handleDragStop);
      };
    }, [componentRef]);

    return (
      <div
        ref={componentRef}
        className={classNames(styles.component, className)}
      >
        {children}
      </div>
    );
  },
);

DragScroll.displayName = 'DragScroll';

export default DragScroll;
